import { z } from "zod";

/**
 * Search params for the cart route.
 *
 * `tab` lives in the URL rather than component state so the comparison matrix
 * can be deep-linked: a concierge link lands the salon on
 * `/cart?tab=review-matrix`, which is the curated cross-supplier view the whole
 * WhatsApp journey exists to show them (KAN-38). An absent `tab` means
 * `by-supplier` (`DEFAULT_CART_TAB`), so a plain `/cart` behaves exactly as before.
 *
 * The default is deliberately applied by the reader, not written into the schema
 * as `.default()`: TanStack Router normalises the URL to the validated search, so
 * a schema default rewrites `/cart` to `/cart?tab=by-supplier` with a second
 * resolved navigation - and a second `$pageview` for every cart load (KAN-254).
 *
 * Kept in its own module so the schema can be tested without pulling in the
 * page's entire component graph.
 */
export const CART_TABS = ["by-supplier", "all-items", "review-matrix"] as const;

export type CartTabValue = (typeof CART_TABS)[number];

export const DEFAULT_CART_TAB: CartTabValue = "by-supplier";

export const cartSearchSchema = z.object({
  // `.catch` rather than a bare enum: an unknown `?tab=` is a stale bookmark or
  // a mangled share, not something to show the salon a route error over. It
  // reads as absent, which means the default view, matching how `/enter`
  // deliberately tolerates a malformed token rather than failing hard.
  tab: z.enum(CART_TABS).optional().catch(undefined)
});

/**
 * Note on unknown params: this schema **strips** them, so `?utm_source=…`
 * does not survive a landing on `/cart`.
 *
 * `.passthrough()` is the obvious fix and was deliberately rejected: it adds an
 * index signature to this route's search type, which propagates into the
 * router-wide union that untyped `navigate({ to, search: prev => … })` calls
 * infer `prev` from. `SearchPage`'s `clearUrlFilters` then sees `prev.q` as
 * `unknown` and stops compiling. Degrading navigation typing across the app is
 * a poor trade for carrying a param this route never reads.
 *
 * If attribution on the cart URL is wanted later, declare the specific keys
 * here rather than opening the schema up.
 */
